// frontend/src/components/search/SearchModalContent.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../common/Icon';
import useDebounce from '../../hooks/useDebounce';
import './SearchModalContent.css';
import apiService from '../../services/api';
import logger from '../../utils/logger';

// Helper components for trending sections
const EventCard = ({ id, title, date, locationName, imageUrl, category, onClose }) => {
  const navigate = useNavigate();
  
  const handleClick = (e) => {
    e.preventDefault();
    logger.info(`[EventCard] Navigating to event: ${id}`);
    onClose?.(); // Close search modal
    navigate(`/event/${id}`); // Navigate to event detail page
  };
  
  return (
    <a href={`/event/${id}`} onClick={handleClick} className="event-card">
      {imageUrl && <img src={imageUrl} alt={title} className="event-image" />}
      <div className="event-content">
        <h4>{title}</h4>
        <p className="event-meta">{date} • {locationName}</p>
        <span className="event-category">{category}</span>
      </div>
    </a>
  );
};

const TaskCard = ({ id, title, description, requesterName, urgency, categoryTags }) => (
  <div className="task-card">
    <Link to={`/task/${id}`} className="task-link">
      <h4>{title}</h4>
      <p className="task-description">{description}</p>
      <div className="task-meta">
        {requesterName && <span className="task-requester">Requested by: {requesterName}</span>}
        {urgency && <span className="task-urgency">Urgency: {urgency}</span>}
      </div>
      <div className="task-categories">
        {categoryTags && categoryTags.map((tag, index) => (
          <span key={index} className="task-category-pill">{tag}</span>
        ))}
      </div>
    </Link>
    <button className="offer-help-button primary-button">Offer Help</button>
  </div>
);

const TrendingPostCard = ({ imageUrl, title, description }) => (
  <div className="trending-post-card">
    <img src={imageUrl} alt={title} className="trending-post-image" />
    <div className="trending-post-content">
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  </div>
);

const TopicPill = ({ name, type = 'topic', href = '#' }) => (
  <a href={href} className={`topic-pill ${type === 'hashtag' ? 'hashtag-pill' : ''}`}>
    {type === 'hashtag' && !name.startsWith('#') ? `#${name}` : name}
  </a>
);

const UserSuggestionCard = ({ avatarUrl, displayName, description, username }) => (
  <div className="user-suggestion-card">
    <Link to={`/profile/${username}`} className="user-profile-link">
      <img src={avatarUrl || '/assets/default_avatar.png'} alt={`${displayName || username}'s avatar`} className="user-avatar" />
      <div className="user-details">
        <h4>{displayName || username}</h4>
        {displayName && <p className="username-text">@{username}</p>}
        {!displayName && username && <p className="username-text">@{username}</p>}
        {description && <p className="user-description">{description}</p>}
      </div>
    </Link>
    <button className="follow-button primary-button">Follow</button>
  </div>
);

const SearchResultCard = ({ type, title, description, user, categoryTags, urgency, id, locationName, date, distance, audioUrl, duration, imageUrl, lat, lng, onClose }) => {
  const navigate = useNavigate();
  let linkPath = '#';
  let displayTitle = title;
  let displayMeta = '';

  switch (type) {
    case 'Event':
      linkPath = `/event/${id}`;
      displayMeta = `${date || ''} ${locationName ? `• ${locationName}` : ''}`;
      break;
    case 'Meeting':
      linkPath = `/meeting/${id}`;
      displayMeta = `${date || ''} ${locationName ? `• ${locationName}` : ''}`;
      break;
    case 'Task':
      linkPath = `/task/${id}`;
      displayMeta = urgency ? `Urgency: ${urgency}` : '';
      break;
    case 'Item':
      linkPath = `/item/${id}`;
      displayMeta = distance ? `${distance.toFixed(1)}km away` : '';
      break;
    case 'User':
      linkPath = `/profile/${user?.username || id}`;
      displayTitle = user?.displayName || user?.username || title;
      displayMeta = user?.username ? `@${user.username}` : '';
      break;
    case 'VoiceNote':
      linkPath = `/voice-note/${id}`;
      displayMeta = `${duration}s ${locationName ? `• ${locationName}` : ''}`;
      break;
    case 'Location':
      linkPath = `/location/${encodeURIComponent(title)}`;
      displayMeta = distance ? `${distance.toFixed(1)}km away` : '';
      break;
    case 'Post':
      linkPath = `/post/${id}`;
      // Add user info if available
      if (user) {
        displayMeta = `@${user.username || 'User'}${locationName ? ` • ${locationName}` : ''}`;
      } else if (locationName) {
        displayMeta = locationName;
      }
      break;
    default:
      break;
  }

  // Click handler that closes modal and navigates
  const handleClick = (e) => {
    e.preventDefault();
    logger.info(`[SearchResultCard] Clicked ${type}: ${id}, navigating to: ${linkPath}`);
    
    // Close modal first
    if (onClose) {
      onClose();
    }
    
    // Small delay to ensure modal closes smoothly
    setTimeout(() => {
      navigate(linkPath);
    }, 100);
  };

  return (
    <a href={linkPath} className="search-result-card" onClick={handleClick}>
      {/* Show user avatar for Posts */}
      {type === 'Post' && user && (
        <img 
          src={user.avatarUrl || '/assets/default_avatar.png'} 
          alt={user.displayName || user.username} 
          className="result-user-avatar" 
        />
      )}
      {imageUrl && <img src={imageUrl} alt={displayTitle} className="result-thumbnail" />}
      <div className="card-content">
        <div className="card-header">
          <h4>{displayTitle}</h4>
          <span className="item-type">{type}</span>
        </div>
        {displayMeta && <p className="card-meta">{displayMeta}</p>}
        <p className="card-description">{description}</p>
        <div className="card-footer">
          {categoryTags && categoryTags.length > 0 && (
            <div className="category-tags">
              {categoryTags.map((tag, index) => <span key={index} className="tag-pill">{tag}</span>)}
            </div>
          )}
          {type === 'VoiceNote' && audioUrl && (
            <div className="voice-note-indicator">
              <Icon name="mic" size="16px" />
              <span>{duration}s</span>
            </div>
          )}
        </div>
      </div>
    </a>
  );
};

const SearchModalContent = ({ onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('Posts');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [error, setError] = useState(null);

  // Trending section states
  const [trendingTopics, setTrendingTopics] = useState([]);
  const [usersToFollow, setUsersToFollow] = useState([]);
  const [trendingEvents, setTrendingEvents] = useState([]);
  const [nearbyTasks, setNearbyTasks] = useState([]);
  const [browseCategories, setBrowseCategories] = useState([]);
  const [isLoadingTrending, setIsLoadingTrending] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const searchTabs = ['Posts', 'Events', 'Meetings', 'Tasks', 'Items', 'Locations', 'Users'];


  // Helper function to strip HTML tags
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Perform search across all categories
  const performSearch = useCallback(async (query, type) => {
    logger.info(`[SearchModalContent] performSearch called. Query: "${query}", Type: "${type}"`);
    
    if (query.trim() === '') {
      logger.info('[SearchModalContent] Query is empty, clearing search results.');
      setSearchResults([]);
      setIsLoadingResults(false);
      return;
    }

    setIsLoadingResults(true);
    setError(null);
    setSearchResults([]);

    try {
      let responseData = [];

      // Search based on active tab
      switch (type) {
        case 'Users':
          logger.info(`[SearchModalContent] Searching users with query: "${query}"`);
          const usersResponse = await apiService.search.searchUsers({ query, limit: 20 });
          console.log('[DEBUG] Full usersResponse:', usersResponse);
          logger.info('[SearchModalContent] Users search response:', usersResponse);
          responseData = usersResponse.users?.map(user => ({
            id: user.id,
            type: 'User',
            title: user.displayName || user.username,
            description: user.bio || '',
            user: { id: user.id, username: user.username, displayName: user.displayName, avatarUrl: user.avatarUrl },
          })) || [];
          break;

        case 'Posts':
          logger.info(`[SearchModalContent] Searching posts with query: "${query}"`);
          const postsResponse = await apiService.search.searchPosts({ query, limit: 20 });
          responseData = postsResponse.posts?.map(post => ({
            id: post.id,
            type: 'Post',
            title: stripHtml(post.title || post.content?.substring(0, 100) || 'Post'),
            description: stripHtml(post.content || ''),
            imageUrl: post.imageUrl,
            locationName: post.locationName,
            user: post.author || post.user,
          })) || [];
          break;

        case 'Events':
          logger.info(`[SearchModalContent] Searching events with query: "${query}"`);
          const eventsResponse = await apiService.search.searchEvents({ query, limit: 20 });
          logger.info('[SearchModalContent] Events response:', eventsResponse);
          responseData = eventsResponse.events?.map(event => ({
            id: event.id,
            type: 'Event',
            title: event.title,
            description: event.description || '',
            locationName: event.locationName || event.venue,
            date: event.date || event.startDate,
            imageUrl: event.imageUrl,
            categoryTags: event.tags || [event.category],
            lat: event.lat || event.latitude,
            lng: event.lng || event.longitude,
          })) || [];
          logger.info(`[SearchModalContent] Processed ${responseData.length} events`);
          break;

        case 'Meetings':
          logger.info(`[SearchModalContent] Searching meetings with query: "${query}"`);
          const meetingsResponse = await apiService.search.searchMeetings({ query, limit: 20 });
          responseData = meetingsResponse.meetings?.map(meeting => ({
            id: meeting.id,
            type: 'Meeting',
            title: meeting.title,
            description: meeting.description || '',
            locationName: meeting.location,
            date: meeting.startDateTime,
          })) || [];
          break;

        case 'Tasks':
          logger.info(`[SearchModalContent] Searching tasks with query: "${query}"`);
          const tasksResponse = await apiService.search.searchTasks({ query, limit: 20 });
          responseData = tasksResponse.tasks?.map(task => ({
            id: task.id,
            type: 'Task',
            title: task.title || task.details?.message || 'Task Request',
            description: task.details?.message || task.description || '',
            urgency: task.details?.urgency || 'Normal',
            categoryTags: [task.type?.replace('_', ' ')],
          })) || [];
          break;

        case 'Items':
          logger.info(`[SearchModalContent] Searching items with query: "${query}"`);
          const itemsResponse = await apiService.search.searchItems({ query, limit: 20 });
          responseData = itemsResponse.items?.map(item => ({
            id: item.id,
            type: 'Item',
            title: item.title,
            description: item.description || '',
            imageUrl: item.images?.[0],
            distance: item.distance,
            categoryTags: [item.category],
          })) || [];
          break;

        case 'Locations':
          logger.info(`[SearchModalContent] Searching locations with query: "${query}"`);
          const locationsResponse = await apiService.search.searchLocations({ query, limit: 20 });
          responseData = locationsResponse.locations?.map(loc => ({
            id: loc.id || loc.name,
            type: 'Location',
            title: loc.name || loc.locationName,
            description: `${loc.totalCount || loc.contentCount || 0} items at this location`,
            distance: loc.distance,
            locationName: loc.address || '',
          })) || [];
          break;

        default:
          logger.warn(`[SearchModalContent] Unknown search type: ${type}`);
          break;
      }

      setSearchResults(responseData);
      logger.info(`[SearchModalContent] Search results updated. Found ${responseData.length} items.`);

    } catch (err) {
      logger.error("SearchModalContent: Search failed:", err?.response?.data || err.message);
      setError("Failed to fetch search results.");
      setSearchResults([]);
    } finally {
      setIsLoadingResults(false);
    }
  }, []);

  // Trigger search when debounced term or tab changes
  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      performSearch(debouncedSearchTerm, activeTab);
    } else {
      setSearchResults([]);
      setIsLoadingResults(false);
    }
  }, [debouncedSearchTerm, activeTab, performSearch]);

  const handleClear = () => {
    setSearchTerm('');
    setSearchResults([]);
  };

  return (
    <div className="search-modal-content">
      <div className="search-modal-header">
        <div className="search-input-group">
          <Icon name="search" size="20px" className="search-icon-input" />
          <input
            type="text"
            placeholder="Search Z..."
            className="search-overlay-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
          {searchTerm && (
            <button className="clear-button" onClick={handleClear}>
              <Icon name="close" size="18px" />
            </button>
          )}
        </div>
        <button className="cancel-button" onClick={onClose}>
          Cancel
        </button>
      </div>

      <div className="search-tabs-container">
        {searchTabs.map(tab => (
          <button
            key={tab}
            className={`search-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="filter-sort-bar">
        <span>Filter/Sort</span>
        <button className="filter-icon-button" aria-label="Filter search results">
          <Icon name="info" size="20px" />
        </button>
      </div>

      <div className="search-results-list">
        {isLoadingResults && <div className="loading-message">Searching...</div>}
        {error && <div className="error-message">{error}</div>}

        {/* Trending sections when no search */}
        {!searchTerm && !isLoadingResults && !error && (
          <div className="trending-discovery-sections">
            {isLoadingTrending && <p className="loading-message">Loading trending content...</p>}
            {!isLoadingTrending && (
              <>
                {trendingTopics.length > 0 && (
                  <section className="search-section">
                    <h2 className="search-section-title">Trending Topics</h2>
                    <div className="topic-pills-container">
                      {trendingTopics.map(topic => <TopicPill key={topic.id} {...topic} />)}
                    </div>
                  </section>
                )}
                {usersToFollow.length > 0 && (
                  <section className="search-section">
                    <h2 className="search-section-title">Who to Follow</h2>
                    <div className="who-to-follow-grid">
                      {usersToFollow.map(user => <UserSuggestionCard key={user.id} {...user} />)}
                    </div>
                  </section>
                )}
                {trendingEvents.length > 0 && (
                  <section className="search-section">
                    <h2 className="search-section-title">Trending Events</h2>
                    <div className="trending-events-carousel">
                      {trendingEvents.map(event => <EventCard key={event.id} {...event} onClose={onClose} />)}
                    </div>
                  </section>
                )}
                {nearbyTasks.length > 0 && (
                  <section className="search-section">
                    <h2 className="search-section-title">Tasks You Can Help With</h2>
                    <div className="nearby-tasks-list">
                      {nearbyTasks.map(task => <TaskCard key={task.id} {...task} />)}
                    </div>
                  </section>
                )}
                {trendingTopics.length === 0 && usersToFollow.length === 0 &&
                 trendingEvents.length === 0 && nearbyTasks.length === 0 && (
                  <div className="no-results-message">Explore what's happening or start typing to search!</div>
                )}
              </>
            )}
          </div>
        )}

        {/* Search results */}
        {searchTerm && searchResults.length === 0 && !isLoadingResults && !error && (
          <div className="no-results-message">No results found for "{searchTerm}" in "{activeTab}".</div>
        )}
        {searchResults.length > 0 && (
          <div className="search-results-items-container">
            {searchResults.map(item => (
              <SearchResultCard key={`${item.type}-${item.id}`} {...item} onClose={onClose} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchModalContent;
